import { apiClient } from "./api";

// Interview, scoring and review calls. All authenticated — the tenant comes
// from the JWT the interceptor attaches, never from anything passed here.
export const interviewService = {
  // ── HR: invite ──────────────────────────────────────────────────────────
  invite: (applicationId, body = {}) =>
    apiClient.post(`/interview/invite/${applicationId}`, body),

  // Reads the CURRENT link back without rotating it.
  //
  // Distinct from `invite` above, and the distinction matters: re-inviting
  // issues a new token and the candidate's existing link dies the moment it
  // does. This is what "copy their link and WhatsApp it" has to call, because
  // the other one breaks the thing you are trying to send.
  interviewLink: (applicationId) =>
    apiClient.get(`/interview/link/${applicationId}`),

  // ── HR: rubric ──────────────────────────────────────────────────────────
  getRubric: (stageId) => apiClient.get(`/rubric/stage/${stageId}`),
  saveRubric: (stageId, rubric) =>
    apiClient.put(`/rubric/stage/${stageId}`, { rubric }),
  rubricVersions: (stageId) => apiClient.get(`/rubric/stage/${stageId}/versions`),

  // ── The questions the interview actually asks ───────────────────────────
  //
  // Per JOB, not per stage and not per candidate. This is the block asked
  // verbatim and identically to everyone, which is the only thing that makes
  // two candidates' scores comparable — the per-candidate questions (resume,
  // deepening) are generated at interview time and are not editable here.
  //
  // NOT the same as jobService's `/jobs/:id/screening-questions` GET, which
  // reads the job TEMPLATE's questions for the manual scoring panel. Different
  // table, different purpose; the interview never reads that one.
  authoredQuestions: (jobId) =>
    apiClient.get(`/jobs/${jobId}/screening-questions/authored`),

  // Drafts from the job description and the rubric's competencies. Saves
  // nothing — the model proposes, a human decides.
  draftQuestions: (jobId, count) =>
    apiClient.post(`/jobs/${jobId}/screening-questions/draft`, { count }),

  // Replaces the whole list. Refuses with 409 once candidates have interviewed
  // on these questions, unless `force` — changing them afterwards would leave
  // recorded answers attached to questions nobody was asked.
  saveQuestions: (jobId, questions, force = false) =>
    apiClient.put(`/jobs/${jobId}/screening-questions`, { questions, force }),

  // ── HR: scoring and review ──────────────────────────────────────────────
  score: (sessionId, rescore = false) =>
    apiClient.post(`/interview/score/${sessionId}`, { rescore }),
  review: (applicationId) => apiClient.get(`/review/session/${applicationId}`),
  // HR sets aside a "Suggest reject" from a failed hard gate, with a reason.
  dismissGate: (applicationId, reason) =>
    apiClient.post(`/review/dismiss-gate/${applicationId}`, { reason }),
  ranking: (jobId, stageId) =>
    apiClient.get(`/review/ranking/${jobId}/${stageId}`),
  decide: (applicationId, decision, reason) =>
    apiClient.post(`/review/decision/${applicationId}`, { decision, reason }),

  // ── HR: pre-interview resume screening ──────────────────────────────────
  //
  // Distinct from `ranking` above, which orders people who have already
  // interviewed. These read resumes for people who have not.
  //
  // `params` may carry { date_field, from, to }. The server filters in SQL so
  // the answer stays right on a job with more applicants than the browser
  // should be asked to hold.
  screeningList: (jobId, params) =>
    apiClient.get(`/screening/job/${jobId}`, params ? { params } : undefined),
  runScreening: (jobId, body = {}) =>
    apiClient.post(`/screening/run/${jobId}`, body),
  rescreenCandidate: (applicationId) =>
    apiClient.post(`/screening/candidate/${applicationId}`),

  // Notice period, salary expectation and years of experience — what somebody
  // was told on the phone. Send only the fields being changed: an absent field
  // is left alone, while null or "" clears it back to "nobody has asked".
  updateDetails: (applicationId, patch) =>
    apiClient.patch(`/screening/details/${applicationId}`, patch),

  // ── WhatsApp pre-screen ─────────────────────────────────────────────────
  //
  // The job's message: a string switches the pre-screen on for the job, null
  // or "" switches it off. Bookkeeping only — the message itself is sent by a
  // person from WhatsApp Web.
  saveWhatsAppTemplate: (jobId, template) =>
    apiClient.put(`/screening/whatsapp-template/${jobId}`, { template }),
  // Where one candidate stands: any of { status, call_slot, note }. Send only
  // what changes; null clears. Every change is also written to their history.
  updateWhatsApp: (applicationId, patch) =>
    apiClient.patch(`/screening/whatsapp/${applicationId}`, patch),

  mailHealth: () => apiClient.get(`/mail/health`),
};

export default interviewService;
